import React, { useState, useEffect } from 'react';
import {
    Table, Button, Input, Tag, Space, Card, message, Typography, Modal, Form,
    Select, InputNumber, DatePicker, Row, Col, Statistic
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined,
    SearchOutlined, BarcodeOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title } = Typography;
const { Search } = Input;
const { confirm } = Modal;

const CATEGORIES = [
    'Antibiotic', 'Painkiller', 'Antiseptic', 'Anesthetic', 'Anti-inflammatory',
    'Vitamin', 'Mouthwash', 'Dental Material', 'Medical Device', 'Other'
];

const Inventory = () => {
    const { t } = useTranslation(['pharmacy', 'common']);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const [filterCategory, setFilterCategory] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);

    useEffect(() => {
        fetchItems();
    }, [filterCategory, filterStatus]);

    const fetchItems = async (search = '') => {
        try {
            setLoading(true);
            let url = '/pharmacy/items?';
            if (search) url += `search=${search}&`;
            if (filterCategory) url += `category=${filterCategory}&`;
            if (filterStatus) url += `status=${filterStatus}&`;
            const response = await api.get(url);
            setItems(response.data.data);
        } catch (error) {
            message.error(t('pharmacy:messages.loadInventoryFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchItems(value);
    };

    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        form.setFieldsValue({ currency: 'IQD', status: 'Active', quantity: 0, reorderLevel: 10 });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        form.setFieldsValue({
            ...record,
            expiryDate: record.expiryDate ? dayjs(record.expiryDate) : null
        });
        setModalVisible(true);
    };

    const handleDelete = (record) => {
        confirm({
            title: t('pharmacy:inventory.deleteTitle', { item: record.itemName }),
            icon: <ExclamationCircleOutlined />,
            content: t('pharmacy:inventory.deleteContent'),
            okType: 'danger',
            onOk: async () => {
                try {
                    await api.delete(`/pharmacy/items/${record._id}`);
                    message.success(t('pharmacy:messages.itemDeleted'));
                    fetchItems(searchText);
                } catch (error) {
                    message.error(t('pharmacy:messages.deleteItemFailed'));
                }
            }
        });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            values.expiryDate = values.expiryDate?.toISOString();

            if (editingItem) {
                await api.put(`/pharmacy/items/${editingItem._id}`, values);
                message.success(t('pharmacy:messages.itemUpdated'));
            } else {
                await api.post('/pharmacy/items', values);
                message.success(t('pharmacy:messages.itemCreated'));
            }
            setModalVisible(false);
            fetchItems(searchText);
        } catch (error) {
            if (error.errorFields) return;
            message.error(error.response?.data?.message || t('pharmacy:messages.saveItemFailed'));
        } finally {
            setSaving(false);
        }
    };

    const totalItems = items.length;
    const lowStock = items.filter(i => i.quantity <= i.reorderLevel && i.quantity > 0).length;
    const outOfStock = items.filter(i => i.quantity === 0).length;
    const expiringSoon = items.filter(i => {
        if (!i.expiryDate) return false;
        const d = new Date(i.expiryDate);
        const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
        return diff <= 30 && diff > 0;
    }).length;

    const columns = [
        {
            title: t('pharmacy:inventory.itemName'),
            dataIndex: 'itemName',
            key: 'itemName',
            render: (text, record) => (
                <Space>
                    <span style={{ fontWeight: 500 }}>{text}</span>
                    {record.quantity <= record.reorderLevel && record.quantity > 0 && (
                        <WarningOutlined style={{ color: '#faad14' }} title={t('pharmacy:inventory.lowStockWarning')} />
                    )}
                </Space>
            ),
            sorter: (a, b) => a.itemName.localeCompare(b.itemName)
        },
        {
            title: t('pharmacy:common.barcode'),
            dataIndex: 'barcode',
            key: 'barcode',
            render: (val) => val ? <Tag icon={<BarcodeOutlined />}>{val}</Tag> : '-'
        },
        {
            title: t('pharmacy:common.sku'),
            dataIndex: 'sku',
            key: 'sku',
        },
        {
            title: t('pharmacy:common.category'),
            dataIndex: 'category',
            key: 'category',
            render: (cat) => <Tag color="blue">{t(`pharmacy:common.categories.${cat}`, cat)}</Tag>
        },
        {
            title: t('pharmacy:common.stock'),
            dataIndex: 'quantity',
            key: 'quantity',
            render: (qty, record) => (
                <Tag color={qty === 0 ? 'red' : qty <= record.reorderLevel ? 'orange' : 'green'}>
                    {qty}
                </Tag>
            ),
            sorter: (a, b) => a.quantity - b.quantity
        },
        {
            title: t('pharmacy:common.costPrice'),
            dataIndex: 'costPrice',
            key: 'costPrice',
            render: (price, record) => record.currency === 'IQD' ? 'IQD ' + price.toLocaleString() : '$' + price.toFixed(2)
        },
        {
            title: t('pharmacy:common.sellingPrice'),
            dataIndex: 'sellingPrice',
            key: 'sellingPrice',
            render: (price, record) => record.currency === 'IQD' ? 'IQD ' + price.toLocaleString() : '$' + price.toFixed(2)
        },
        {
            title: t('pharmacy:inventory.expiry'),
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            render: (date) => {
                if (!date) return '-';
                const diff = (new Date(date) - new Date()) / (1000 * 60 * 60 * 24);
                const color = diff <= 0 ? 'red' : diff <= 30 ? 'orange' : 'default';
                return <Tag color={color}>{dayjs(date).format('MMM DD, YYYY')}</Tag>;
            },
            sorter: (a, b) => new Date(a.expiryDate) - new Date(b.expiryDate)
        },
        {
            title: t('pharmacy:common.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'Active' ? 'green' : status === 'Out of Stock' ? 'red' : 'default'}>
                    {t(`pharmacy:common.itemStatuses.${status}`, status)}
                </Tag>
            )
        },
        {
            title: t('pharmacy:common.actions'),
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                        {t('pharmacy:common.edit')}
                    </Button>
                    <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
                        {t('pharmacy:common.delete')}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>{t('pharmacy:inventory.title')}</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    {t('pharmacy:inventory.addItem')}
                </Button>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                    <Card><Statistic title={t('pharmacy:inventory.totalItems')} value={totalItems} /></Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card><Statistic title={t('pharmacy:inventory.lowStock')} value={lowStock} valueStyle={{ color: '#faad14' }} /></Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card><Statistic title={t('pharmacy:inventory.outOfStock')} value={outOfStock} valueStyle={{ color: '#ff4d4f' }} /></Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card><Statistic title={t('pharmacy:inventory.expiringSoon')} value={expiringSoon} valueStyle={{ color: '#fa8c16' }} /></Card>
                </Col>
            </Row>

            <Card style={{ marginBottom: 16 }} size="small">
                <Space wrap>
                    <Search
                        placeholder={t('pharmacy:inventory.searchPlaceholder')}
                        enterButton={<SearchOutlined />}
                        style={{ width: 300 }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onSearch={handleSearch}
                        allowClear
                    />
                    <Select style={{ width: 160 }} placeholder={t('pharmacy:common.category')} allowClear onChange={setFilterCategory}>
                        {CATEGORIES.map(c => <Select.Option key={c} value={c}>{t(`pharmacy:common.categories.${c}`, c)}</Select.Option>)}
                    </Select>
                    <Select style={{ width: 140 }} placeholder={t('pharmacy:common.status')} allowClear onChange={setFilterStatus}>
                        <Select.Option value="Active">{t('pharmacy:common.itemStatuses.Active')}</Select.Option>
                        <Select.Option value="Out of Stock">{t('pharmacy:common.itemStatuses.Out of Stock')}</Select.Option>
                        <Select.Option value="Discontinued">{t('pharmacy:common.itemStatuses.Discontinued')}</Select.Option>
                    </Select>
                </Space>
            </Card>

            <Card>
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="_id"
                    loading={loading}
                    locale={{ emptyText: t('pharmacy:inventory.noItems') }}
                    pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (total) => t('pharmacy:inventory.totalCount', { total }) }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>

            <Modal
                title={editingItem ? t('pharmacy:inventory.editItem') : t('pharmacy:inventory.addNewItem')}
                open={modalVisible}
                onOk={handleSave}
                onCancel={() => setModalVisible(false)}
                width={700}
                confirmLoading={saving}
                okText={editingItem ? t('pharmacy:common.update') : t('pharmacy:common.create')}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="itemName" label={t('pharmacy:inventory.itemName')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="barcode" label={t('pharmacy:common.barcode')}>
                                <Input prefix={<BarcodeOutlined />} placeholder={t('pharmacy:inventory.scanBarcode')} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="sku" label={t('pharmacy:common.sku')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="category" label={t('pharmacy:common.category')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <Select>
                                    {CATEGORIES.map(c => <Select.Option key={c} value={c}>{t(`pharmacy:common.categories.${c}`, c)}</Select.Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="currency" label={t('pharmacy:common.currency')} rules={[{ required: true }]}>
                                <Select>
                                    <Select.Option value="IQD">IQD</Select.Option>
                                    <Select.Option value="USD">USD</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item name="costPrice" label={t('pharmacy:common.costPrice')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="sellingPrice" label={t('pharmacy:common.sellingPrice')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="quantity" label={t('pharmacy:inventory.quantity')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="reorderLevel" label={t('pharmacy:inventory.reorderLevel')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="expiryDate" label={t('pharmacy:inventory.expiryDate')} rules={[{ required: true, message: t('pharmacy:common.required') }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="manufacturer" label={t('pharmacy:inventory.manufacturer')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="batchNumber" label={t('pharmacy:inventory.batchNumber')}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="location" label={t('pharmacy:inventory.storageLocation')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="status" label={t('pharmacy:common.status')}>
                                <Select>
                                    <Select.Option value="Active">{t('pharmacy:common.itemStatuses.Active')}</Select.Option>
                                    <Select.Option value="Discontinued">{t('pharmacy:common.itemStatuses.Discontinued')}</Select.Option>
                                    <Select.Option value="Out of Stock">{t('pharmacy:common.itemStatuses.Out of Stock')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="description" label={t('pharmacy:inventory.description')}>
                                <Input.TextArea rows={1} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};

export default Inventory;
